import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUserStatus } from "@/hooks/useUserStatus";
import { ROUTE_PATH } from "@/constants/routeConstants";

interface UserStatusGuardProps {
  children: React.ReactNode;
}

/**
 * UserStatusGuard Component
 *
 * Wraps protected routes to ensure user status is verified before access
 *
 * Features:
 * - Fetches user status from /user/status API
 * - Shows loading state while fetching
 * - Redirects to account inactive page if user is not active
 * - Shows error state if API fails
 * - Only renders children if user is active
 *
 * Usage:
 * <UserStatusGuard>
 *   <Dashboard />
 * </UserStatusGuard>
 */
const UserStatusGuard: React.FC<UserStatusGuardProps> = ({ children }) => {
  const navigate = useNavigate();
  const { isLoading, isActive, error, isFirstCheck, isNoAgenciesError } = useUserStatus();

  useEffect(() => {
    // Wait for initial loading to complete before making navigation decisions
    if (isLoading) {
      return;
    }

    // If user has no agencies assigned (403 error), redirect to dashboard instead of AccountInactive
    if (isNoAgenciesError) {
      navigate(ROUTE_PATH.ROUTE_DASHBOARD, { replace: true });
      return;
    }
    
    // If there's an error, redirect to AccountInactive with error message
    if (error) {
      navigate(ROUTE_PATH.ROUTE_ACCOUNT_INACTIVE, { 
        replace: true,
        state: { error: error }
      });
      return;
    }
    
    // If status has been checked and user is inactive, redirect
    if (isActive === false) {
      navigate(ROUTE_PATH.ROUTE_ACCOUNT_INACTIVE, { replace: true });
    }
  }, [isActive, isNoAgenciesError, error, isLoading, navigate]);

  // Show loading state while checking status
  if (isLoading || isActive === null) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-background'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4'></div>
          {/* Only show text on first login, hide on page reloads */}
          {isFirstCheck && (
            <>
              <p className='text-lg font-medium text-foreground'>
                Verifying account status...
              </p>
              <p className='text-sm text-muted-foreground mt-2'>
                Please wait while we check your account
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  // User is active, render children
  if (isActive === true) {
    return <>{children}</>;
  }

  // Fallback - should not reach here due to useEffect redirects above
  // But keep it as a safety net
  return null;
};

export default UserStatusGuard;